import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { TabsModule } from 'primeng/tabs';
import { TagModule } from 'primeng/tag';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { CurrencySelectorComponent } from '../../shared/design-system/currency-selector.component';
import { AinAmountPipe } from '../../shared/design-system/ain-amount.pipe';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { FinanceApiService } from '../finance/finance-api.service';
import { FinancialAccountSelectorComponent } from '../../shared/design-system/financial-account-selector.component';
import { PAYMENTS_LIST_PAGE_STYLES, paymentStatusLabelKey } from './payments-list-page.utils';
import { AinCanAccessDirective } from '../../shared/security/access.directive';
import {
  CreateFunderProfilePayload,
  CreateFundingPoolPayload,
  FunderExposureSummaryRow,
  FunderProfileRow,
  FundingPoolRow,
  FundingPoolWaterfallPolicyRow,
  LoanContractRow,
  PaymentsAdminService,
  PortfolioAllocationRow,
  PortfolioRepaymentDistributionRow,
  UpsertFundingPoolWaterfallPolicyPayload
} from './payments-admin.service';

@Component({
  standalone: true,
  selector: 'ain-payments-portfolio',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    FormsModule,
    TableModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    SelectModule,
    TabsModule,
    TagModule,
    RouterLink,
    AinCanAccessDirective,
    CurrencySelectorComponent,
    FinancialAccountSelectorComponent,
    AinAmountPipe
  ],
  template: `
    <ain-page [eyebrow]="t('payments.eyebrow')" [title]="t('payments.portfolio.title')" [description]="t('payments.portfolio.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'payments.portfolio.help.page.title'"
        [descriptionKey]="'payments.portfolio.help.page.description'"
        [bulletKeys]="[
          'payments.portfolio.help.page.bullets.funders',
          'payments.portfolio.help.page.bullets.pools',
          'payments.portfolio.help.page.bullets.allocations'
        ]" />

      @if (loading()) {
        <ain-state type="loading" [title]="t('payments.portfolio.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('payments.portfolio.unavailable')" [message]="error() ?? ''" />
      } @else {
        <section class="card panel">
          <div class="toolbar">
            @if (activeTab() === 'funders') {
              <button
                pButton
                type="button"
                [label]="t('common.create')"
                ainCanAccess
                [ainCanAccessPermission]="'payments.portfolio.create'"
                (click)="openCreateFunderDialog()">
              </button>
            }
            @if (activeTab() === 'pools') {
              <button
                pButton
                type="button"
                [label]="t('common.create')"
                ainCanAccess
                [ainCanAccessPermission]="'payments.portfolio.create'"
                (click)="openCreatePoolDialog()">
              </button>
            }
            @if (activeTab() === 'allocations') {
              <button
                pButton
                type="button"
                [label]="t('payments.portfolio.allocate')"
                ainCanAccess
                [ainCanAccessPermission]="'payments.portfolio.create'"
                (click)="openAllocateDialog()">
              </button>
            }
            <button pButton type="button" severity="secondary" [label]="t('common.refresh')" (click)="reload()"></button>
          </div>

          <details class="filters" [open]="true">
            <summary>
              <span>{{ t('payments.portfolio.filters.title') }}</span>
              <ain-info-hint
                [titleKey]="'payments.portfolio.help.filters.title'"
                [descriptionKey]="'payments.portfolio.help.filters.description'" />
            </summary>
            <div class="filter-grid">
              <label>
                <span>{{ t('payments.portfolio.filterFunder') }}</span>
                <p-select
                  [options]="funderFilterOptions()"
                  [(ngModel)]="filterFunderId"
                  optionLabel="label"
                  optionValue="value"
                  [showClear]="true"
                  [filter]="true"
                  appendTo="body"
                  [placeholder]="t('payments.portfolio.filterFunder')">
                </p-select>
              </label>
              <label>
                <span>{{ t('payments.portfolio.filterStatus') }}</span>
                <p-select
                  [options]="statusFilterOptions"
                  [(ngModel)]="filterStatus"
                  optionValue="value"
                  [showClear]="true"
                  appendTo="body"
                  [placeholder]="t('payments.portfolio.filterStatus')">
                  <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : t('common.all') }}</ng-template>
                  <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
                </p-select>
              </label>
              <label>
                <span>{{ t('columns.currency') }}</span>
                <ain-currency-selector [(ngModel)]="filterCurrency"></ain-currency-selector>
              </label>
            </div>
          </details>

          @if (actionError()) {
            <ain-state type="error" [title]="t('payments.portfolio.actionError')" [message]="actionError() ?? ''" />
          }
        @if (exposureSummaryCards().length) {
          <div class="section-title">
            <span>{{ t('payments.portfolio.help.summary.title') }}</span>
            <ain-info-hint
              [titleKey]="'payments.portfolio.help.summary.title'"
              [descriptionKey]="'payments.portfolio.help.summary.description'" />
          </div>
          <section class="summary-grid">
            @for (card of exposureSummaryCards(); track card.key) {
              <div>
                <span>{{ card.label }}</span>
                <strong>{{ card.value | ainAmount }}</strong>
              </div>
            }
          </section>
        }

        <p-tabs [value]="activeTab()" (valueChange)="onTabChanged($event)">
          <p-tablist>
            <p-tab value="funders">{{ t('payments.portfolio.tabs.funders') }}</p-tab>
            <p-tab value="pools">{{ t('payments.portfolio.tabs.pools') }}</p-tab>
            <p-tab value="allocations">{{ t('payments.portfolio.tabs.allocations') }}</p-tab>
            <p-tab value="exposure">{{ t('payments.portfolio.tabs.exposure') }}</p-tab>
            <p-tab value="distributions">{{ t('payments.portfolio.tabs.distributions') }}</p-tab>
          </p-tablist>
          <p-tabpanels>
            <p-tabpanel value="funders">
              <div class="section-title">
                <span>{{ t('payments.portfolio.tabs.funders') }}</span>
                <ain-info-hint
                  [titleKey]="'payments.portfolio.help.tabs.funders.title'"
                  [descriptionKey]="'payments.portfolio.help.tabs.funders.description'" />
              </div>
              <p-table [value]="filteredFunders()">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('payments.portfolio.funderCode') }}</th>
                    <th>{{ t('payments.portfolio.displayName') }}</th>
                    <th>{{ t('columns.status') }}</th>
                    <th>{{ t('payments.portfolio.actions') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ row.funderCode }}</td>
                    <td>{{ row.displayName }}</td>
                    <td><p-tag [value]="statusLabel(row.status)" [severity]="statusSeverity(row.status)"></p-tag></td>
                    <td class="actions">
                      @if (row.status !== 'Active') {
                        <button pButton type="button" size="small" [label]="t('payments.portfolio.activate')" (click)="activateFunder(row)"></button>
                      }
                      @if (row.status === 'Active') {
                        <button pButton type="button" size="small" severity="warn" [label]="t('payments.portfolio.suspend')" (click)="suspendFunder(row)"></button>
                      }
                    </td>
                  </tr>
                </ng-template>
                <ng-template pTemplate="emptymessage">
                  <tr><td colspan="4">{{ t('payments.portfolio.emptyFunders') }}</td></tr>
                </ng-template>
              </p-table>
            </p-tabpanel>

            <p-tabpanel value="pools">
              <div class="section-title">
                <span>{{ t('payments.portfolio.tabs.pools') }}</span>
                <ain-info-hint
                  [titleKey]="'payments.portfolio.help.tabs.pools.title'"
                  [descriptionKey]="'payments.portfolio.help.tabs.pools.description'" />
              </div>
              <p-table [value]="filteredPools()" dataKey="id">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('payments.portfolio.poolCode') }}</th>
                    <th>{{ t('payments.portfolio.funderCode') }}</th>
                    <th>{{ t('columns.currency') }}</th>
                    <th>{{ t('payments.portfolio.availableCapital') }}</th>
                    <th>{{ t('payments.portfolio.allocatedCapital') }}</th>
                    <th>{{ t('columns.status') }}</th>
                    <th>{{ t('payments.portfolio.actions') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr
                    class="selectable-row"
                    [class.selected-row]="selectedPool()?.id === row.id"
                    (click)="onPoolSelected(row)">
                    <td>{{ row.poolCode }}</td>
                    <td>{{ funderCode(row.funderId) }}</td>
                    <td>{{ row.currencyCode }}</td>
                    <td>{{ row.availableCapital | ainAmount }}</td>
                    <td>{{ row.allocatedCapital | ainAmount }}</td>
                    <td><p-tag [value]="statusLabel(row.status)" [severity]="statusSeverity(row.status)"></p-tag></td>
                    <td class="actions">
                      <button
                        pButton
                        type="button"
                        size="small"
                        severity="secondary"
                        [label]="t('payments.portfolio.configureWaterfall')"
                        ainCanAccess
                        [ainCanAccessPermission]="'payments.portfolio.update'"
                        (click)="onPoolSelected(row); openWaterfallDialog(); $event.stopPropagation()">
                      </button>
                      @if (row.status !== 'Active') {
                        <button pButton type="button" size="small" [label]="t('payments.portfolio.activate')" (click)="activatePool(row); $event.stopPropagation()"></button>
                      }
                      @if (row.status === 'Active') {
                        <button pButton type="button" size="small" severity="warn" [label]="t('payments.portfolio.freeze')" (click)="freezePool(row); $event.stopPropagation()"></button>
                      }
                      @if (row.status !== 'Closed') {
                        <button pButton type="button" size="small" severity="danger" [label]="t('payments.portfolio.close')" (click)="closePool(row); $event.stopPropagation()"></button>
                      }
                    </td>
                  </tr>
                </ng-template>
                <ng-template pTemplate="emptymessage">
                  <tr><td colspan="7">{{ t('payments.portfolio.emptyPools') }}</td></tr>
                </ng-template>
              </p-table>

              @if (selectedPool()) {
                <section class="detail-card">
                  <h4>{{ t('payments.portfolio.poolDetail') }} — {{ selectedPool()!.poolCode }}</h4>
                  <p>{{ t('payments.portfolio.committedCapital') }}: {{ selectedPool()!.committedCapital | ainAmount }} · {{ t('payments.portfolio.repaidCapital') }}: {{ selectedPool()!.repaidCapital | ainAmount }}</p>
                  @if (selectedWaterfall()) {
                    <p>{{ t('payments.portfolio.waterfallActive') }}: {{ selectedWaterfall()!.policyType }} · {{ selectedWaterfall()!.policyJson }}</p>
                  } @else {
                    <p>{{ t('payments.portfolio.noWaterfall') }}</p>
                  }
                </section>
              }
            </p-tabpanel>

            <p-tabpanel value="allocations">
              <div class="section-title">
                <span>{{ t('payments.portfolio.tabs.allocations') }}</span>
                <ain-info-hint
                  [titleKey]="'payments.portfolio.help.tabs.allocations.title'"
                  [descriptionKey]="'payments.portfolio.help.tabs.allocations.description'" />
              </div>
              <p-table
                [value]="filteredAllocations()"
                dataKey="id"
                [paginator]="true"
                [rows]="allocationPageSize"
                [totalRecords]="allocationTotalRecords"
                [lazy]="true"
                (onLazyLoad)="onAllocationPage($event)">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('payments.portfolio.poolCode') }}</th>
                    <th>{{ t('payments.portfolio.loan') }}</th>
                    <th>{{ t('payments.portfolio.principalAllocated') }}</th>
                    <th>{{ t('columns.status') }}</th>
                    <th>{{ t('payments.portfolio.actions') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr
                    class="selectable-row"
                    [class.selected-row]="selectedAllocation()?.id === row.id"
                    (click)="selectedAllocation.set(row)">
                    <td>{{ poolCode(row.fundingPoolId) }}</td>
                    <td><code>{{ row.loanContractId }}</code></td>
                    <td>{{ row.principalAllocated | ainAmount }} {{ row.currencyCode }}</td>
                    <td><p-tag [value]="statusLabel(row.status)" [severity]="statusSeverity(row.status)"></p-tag></td>
                    <td class="actions">
                      @if (row.status === 'Active') {
                        <button pButton type="button" size="small" severity="warn" [label]="t('payments.portfolio.release')" (click)="releaseAllocation(row); $event.stopPropagation()"></button>
                      }
                      <a [routerLink]="['/payments/bnpl']" (click)="$event.stopPropagation()">{{ t('payments.portfolio.viewLoan') }}</a>
                    </td>
                  </tr>
                </ng-template>
                <ng-template pTemplate="emptymessage">
                  <tr><td colspan="5">{{ t('payments.portfolio.emptyAllocations') }}</td></tr>
                </ng-template>
              </p-table>

              @if (selectedAllocation()) {
                <section class="detail-card">
                  <h4>{{ t('payments.portfolio.allocationDetail') }}</h4>
                  <p>{{ t('payments.lending.idempotencyKey') }}: {{ selectedAllocation()!.idempotencyKey }}</p>
                  <p>{{ t('payments.portfolio.allocationPercent') }}: {{ selectedAllocation()!.allocationPercent }}</p>
                  <p>{{ t('payments.portfolio.allocatedAt') }}: {{ selectedAllocation()!.allocatedAtUtc }}</p>
                </section>
              }
            </p-tabpanel>

            <p-tabpanel value="exposure">
              <div class="section-title">
                <span>{{ t('payments.portfolio.tabs.exposure') }}</span>
                <ain-info-hint
                  [titleKey]="'payments.portfolio.help.tabs.exposure.title'"
                  [descriptionKey]="'payments.portfolio.help.tabs.exposure.description'" />
              </div>
              <p-table [value]="filteredExposure()">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('payments.portfolio.funderCode') }}</th>
                    <th>{{ t('columns.currency') }}</th>
                    <th>{{ t('payments.portfolio.totalAllocated') }}</th>
                    <th>{{ t('payments.portfolio.outstandingPrincipal') }}</th>
                    <th>{{ t('payments.portfolio.repaidPrincipal') }}</th>
                    <th>{{ t('payments.portfolio.delinquentExposure') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td>{{ funderCode(row.funderId) }}</td>
                    <td>{{ row.currencyCode }}</td>
                    <td>{{ row.totalAllocated | ainAmount }}</td>
                    <td>{{ row.outstandingPrincipal | ainAmount }}</td>
                    <td>{{ row.repaidPrincipal | ainAmount }}</td>
                    <td>{{ row.delinquentExposure | ainAmount }}</td>
                  </tr>
                </ng-template>
                <ng-template pTemplate="emptymessage">
                  <tr><td colspan="6">{{ t('payments.portfolio.emptyExposure') }}</td></tr>
                </ng-template>
              </p-table>
            </p-tabpanel>

            <p-tabpanel value="distributions">
              <div class="section-title">
                <span>{{ t('payments.portfolio.tabs.distributions') }}</span>
                <ain-info-hint
                  [titleKey]="'payments.portfolio.help.tabs.distributions.title'"
                  [descriptionKey]="'payments.portfolio.help.tabs.distributions.description'" />
              </div>
              <p-table
                [value]="filteredDistributions()"
                [paginator]="true"
                [rows]="distributionPageSize"
                [totalRecords]="distributionTotalRecords"
                [lazy]="true"
                (onLazyLoad)="onDistributionPage($event)">
                <ng-template pTemplate="header">
                  <tr>
                    <th>{{ t('payments.portfolio.loan') }}</th>
                    <th>{{ t('payments.portfolio.poolCode') }}</th>
                    <th>{{ t('payments.portfolio.amount') }}</th>
                    <th>{{ t('payments.portfolio.distributedAt') }}</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-row>
                  <tr>
                    <td><code>{{ row.loanContractId }}</code></td>
                    <td>{{ poolCode(row.fundingPoolId) }}</td>
                    <td>{{ row.amount | ainAmount }} {{ row.currencyCode }}</td>
                    <td>{{ row.distributedAtUtc }}</td>
                  </tr>
                </ng-template>
                <ng-template pTemplate="emptymessage">
                  <tr><td colspan="4">{{ t('payments.portfolio.emptyDistributions') }}</td></tr>
                </ng-template>
              </p-table>
            </p-tabpanel>
          </p-tabpanels>
        </p-tabs>
        </section>
      }

      <p-dialog
        [header]="t('payments.portfolio.createFunderTitle')"
        [modal]="true"
        [(visible)]="createFunderDialogVisible"
        [style]="{ width: '560px' }">
        <div class="dialog-grid">
          <label>
            <span class="label-row">
              {{ t('payments.portfolio.funderCode') }}
              <ain-info-hint
                [titleKey]="'payments.portfolio.help.createFunder.title'"
                [descriptionKey]="'payments.portfolio.help.createFunder.description'" />
            </span>
            <input pInputText [(ngModel)]="funderModel.funderCode" name="createFunderCode" />
          </label>
          <label>
            <span>{{ t('payments.portfolio.displayName') }}</span>
            <input pInputText [(ngModel)]="funderModel.displayName" name="createFunderDisplayName" />
          </label>
          <label class="wide">
            <span>{{ t('payments.portfolio.complianceReference') }}</span>
            <input pInputText [(ngModel)]="funderModel.complianceReference" name="createFunderCompliance" />
          </label>
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="createFunderDialogVisible = false"></button>
          <button pButton type="button" [label]="t('common.save')" [disabled]="creatingFunder()" (click)="createFunder()"></button>
        </ng-template>
      </p-dialog>

      <p-dialog
        [header]="t('payments.portfolio.createPoolTitle')"
        [modal]="true"
        [(visible)]="createPoolDialogVisible"
        [style]="{ width: '640px' }">
        <div class="dialog-grid">
          <label class="wide">
            <span class="label-row">
              {{ t('payments.portfolio.selectFunder') }}
              <ain-info-hint
                [titleKey]="'payments.portfolio.help.createPool.title'"
                [descriptionKey]="'payments.portfolio.help.createPool.description'" />
            </span>
            <p-select
              [options]="funderSelectOptions()"
              [(ngModel)]="poolModel.funderId"
              name="createPoolFunderId"
              optionLabel="label"
              optionValue="value"
              [filter]="true"
              appendTo="body"
              [placeholder]="t('payments.portfolio.selectFunder')">
            </p-select>
          </label>
          <label>
            <span>{{ t('payments.portfolio.poolCode') }}</span>
            <input pInputText [(ngModel)]="poolModel.poolCode" name="createPoolCode" />
          </label>
          <label>
            <span>{{ t('columns.currency') }}</span>
            <ain-currency-selector [(ngModel)]="poolModel.currencyCode" name="createPoolCurrency"></ain-currency-selector>
          </label>
          <label class="wide">
            <span>{{ t('payments.portfolio.committedCapital') }}</span>
            <input pInputText type="number" [(ngModel)]="poolModel.committedCapital" name="createPoolCommittedCapital" />
          </label>
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="createPoolDialogVisible = false"></button>
          <button pButton type="button" [label]="t('common.save')" [disabled]="creatingPool()" (click)="createPool()"></button>
        </ng-template>
      </p-dialog>

      <p-dialog
        [header]="t('payments.portfolio.createAllocationTitle')"
        [modal]="true"
        [(visible)]="allocateDialogVisible"
        [style]="{ width: '640px' }">
        <div class="dialog-grid">
          <label class="wide">
            <span class="label-row">
              {{ t('payments.portfolio.selectPool') }}
              <ain-info-hint
                [titleKey]="'payments.portfolio.help.createAllocation.title'"
                [descriptionKey]="'payments.portfolio.help.createAllocation.description'" />
            </span>
            <p-select
              [options]="poolSelectOptions()"
              [(ngModel)]="allocateModel.fundingPoolId"
              name="allocatePoolId"
              optionLabel="label"
              optionValue="value"
              [filter]="true"
              appendTo="body"
              [placeholder]="t('payments.portfolio.selectPool')">
            </p-select>
          </label>
          <label class="wide">
            <span class="label-row">
              {{ t('payments.portfolio.selectLoan') }}
              <ain-info-hint
                [titleKey]="'payments.portfolio.help.selectLoan.title'"
                [descriptionKey]="'payments.portfolio.help.selectLoan.description'" />
            </span>
            <p-select
              [options]="loanSelectOptions()"
              [(ngModel)]="allocateModel.loanContractId"
              name="allocateLoanId"
              optionLabel="label"
              optionValue="value"
              [filter]="true"
              appendTo="body"
              (ngModelChange)="onLoanSelected($event)"
              [placeholder]="t('payments.portfolio.selectLoan')">
            </p-select>
            @if (loanSelectOptions().length === 0) {
              <p class="field-hint">{{ t('payments.portfolio.emptyBnplContractsMessage') }}</p>
              <a [routerLink]="['/payments/bnpl']">{{ t('payments.portfolio.openBnplContracts') }}</a>
            }
          </label>
          <label>
            <span>{{ t('payments.portfolio.principalAllocated') }}</span>
            <input pInputText type="number" [(ngModel)]="allocateModel.principalAllocated" name="allocatePrincipal" />
          </label>
          <label>
            <span>{{ t('payments.portfolio.allocationPercent') }}</span>
            <input pInputText type="number" [(ngModel)]="allocateModel.allocationPercent" name="allocatePercent" />
          </label>
          <label class="wide">
            <span class="label-row">
              {{ t('payments.portfolio.allocationPosting') }}
              <ain-info-hint
                [titleKey]="'payments.portfolio.help.allocationPosting.title'"
                [descriptionKey]="'payments.portfolio.help.allocationPosting.description'" />
            </span>
            <p-select [options]="bookOptions()" [(ngModel)]="postingBookId" name="allocateBookId" optionLabel="label" optionValue="value" appendTo="body" (ngModelChange)="onBookChanged()" [placeholder]="t('finance.journal.book')"></p-select>
          </label>
          <label class="wide">
            <span>{{ t('finance.journal.period') }}</span>
            <p-select [options]="periodOptions()" [(ngModel)]="postingPeriodId" name="allocatePeriodId" optionLabel="label" optionValue="value" appendTo="body" [placeholder]="t('finance.journal.period')"></p-select>
          </label>
          <label class="wide">
            <span>{{ t('payments.financialCompleteness.debitAccount') }}</span>
            <ain-financial-account-selector [(ngModel)]="postingDebitAccountId" name="allocateDebitAccountId" [bookId]="postingBookId"></ain-financial-account-selector>
          </label>
          <label class="wide">
            <span>{{ t('payments.financialCompleteness.creditAccount') }}</span>
            <ain-financial-account-selector [(ngModel)]="postingCreditAccountId" name="allocateCreditAccountId" [bookId]="postingBookId"></ain-financial-account-selector>
          </label>
          <label>
            <span>{{ t('columns.currency') }}</span>
            <ain-currency-selector [(ngModel)]="postingCurrencyCode" name="allocateCurrencyCode"></ain-currency-selector>
          </label>
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="allocateDialogVisible = false"></button>
          <button pButton type="button" [label]="t('payments.portfolio.allocate')" [disabled]="creatingAllocation()" (click)="allocateLoan()"></button>
        </ng-template>
      </p-dialog>

      <p-dialog
        [header]="t('payments.portfolio.waterfallDialogTitle')"
        [modal]="true"
        [(visible)]="waterfallDialogVisible"
        [style]="{ width: '640px' }">
        <div class="dialog-grid">
          <label class="wide">
            <span class="label-row">
              {{ t('payments.portfolio.waterfallPolicyType') }}
              <ain-info-hint
                [titleKey]="'payments.portfolio.help.waterfall.title'"
                [descriptionKey]="'payments.portfolio.help.waterfall.description'" />
            </span>
            <p-select
              [options]="waterfallPolicyTypeOptions"
              [(ngModel)]="waterfallModel.policyType"
              name="waterfallPolicyType"
              optionValue="value"
              appendTo="body"
              [placeholder]="t('payments.portfolio.waterfallPolicyType')">
              <ng-template pTemplate="selectedItem" let-item>{{ item ? t(item.labelKey) : '' }}</ng-template>
              <ng-template pTemplate="item" let-item>{{ t(item.labelKey) }}</ng-template>
            </p-select>
          </label>
          <label class="wide">
            <span>{{ t('payments.portfolio.waterfallPolicyJson') }}</span>
            <input pInputText [(ngModel)]="waterfallModel.policyJson" name="waterfallPolicyJson" />
          </label>
        </div>
        <ng-template pTemplate="footer">
          <button pButton type="button" severity="secondary" [label]="t('common.cancel')" (click)="waterfallDialogVisible = false"></button>
          <button pButton type="button" [label]="t('payments.portfolio.saveWaterfall')" [disabled]="savingWaterfall()" (click)="saveWaterfall()"></button>
        </ng-template>
      </p-dialog>
    </ain-page>
  `,
  styles: [`
    .label-row { display: inline-flex; align-items: center; gap: 6px; }
    .field-hint { margin: 6px 0 0; color: #64748b; font-size: 12px; line-height: 1.6; }
    .actions { display: flex; gap: 6px; flex-wrap: wrap; align-items: center; }
    .detail-card { margin-top: 12px; padding: 12px; border: 1px solid #e2e8f0; border-radius: 8px; }
    .summary-grid span { color: #64748b; font-size: 12px; }
    .selectable-row { cursor: pointer; }
    .selected-row { background: #eff6ff; }
    ${PAYMENTS_LIST_PAGE_STYLES}
  `]
})
export class PaymentsPortfolioComponent implements OnInit {
  private readonly api = inject(PaymentsAdminService);
  private readonly financeApi = inject(FinanceApiService);
  private readonly localizationService = inject(LocalizationService);

  readonly funders = signal<FunderProfileRow[]>([]);
  readonly pools = signal<FundingPoolRow[]>([]);
  readonly allocations = signal<PortfolioAllocationRow[]>([]);
  readonly exposure = signal<FunderExposureSummaryRow[]>([]);
  readonly distributions = signal<PortfolioRepaymentDistributionRow[]>([]);
  readonly loanContracts = signal<LoanContractRow[]>([]);
  readonly selectedPool = signal<FundingPoolRow | null>(null);
  readonly selectedWaterfall = signal<FundingPoolWaterfallPolicyRow | null>(null);
  readonly selectedAllocation = signal<PortfolioAllocationRow | null>(null);
  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly actionError = signal<string | null>(null);
  readonly creatingFunder = signal(false);
  readonly creatingPool = signal(false);
  readonly creatingAllocation = signal(false);
  readonly savingWaterfall = signal(false);
  readonly activeTab = signal('funders');

  createFunderDialogVisible = false;
  createPoolDialogVisible = false;
  allocateDialogVisible = false;
  waterfallDialogVisible = false;

  filterFunderId: string | null = null;
  filterStatus = '';
  filterCurrency = 'IRR';
  allocationPage = 1;
  allocationPageSize = 25;
  allocationTotalRecords = 0;
  distributionPage = 1;
  distributionPageSize = 25;
  distributionTotalRecords = 0;

  funderModel = { funderCode: '', displayName: '', complianceReference: '' };
  poolModel = { funderId: '', poolCode: '', currencyCode: 'IRR', committedCapital: 10000 };
  allocateModel = { fundingPoolId: '', loanContractId: '', customerAccountId: '', principalAllocated: 0, allocationPercent: 100 };
  waterfallModel = { policyType: 'ProRata', policyJson: '{"strategy":"pro-rata"}' };
  bookOptions = signal<{ label: string; value: string }[]>([]);
  periodOptions = signal<{ label: string; value: string }[]>([]);
  postingBookId = '';
  postingPeriodId = '';
  postingDebitAccountId = '';
  postingCreditAccountId = '';
  postingCurrencyCode = 'IRR';

  readonly statusFilterOptions = [
    { labelKey: 'payments.status.Active', value: 'Active' },
    { labelKey: 'payments.status.Suspended', value: 'Suspended' },
    { labelKey: 'payments.status.Frozen', value: 'Frozen' },
    { labelKey: 'payments.status.Closed', value: 'Closed' },
    { labelKey: 'payments.status.Released', value: 'Released' }
  ];

  readonly waterfallPolicyTypeOptions = [
    { labelKey: 'payments.portfolio.waterfallPolicyTypes.ProRata', value: 'ProRata' },
    { labelKey: 'payments.portfolio.waterfallPolicyTypes.Sequential', value: 'Sequential' }
  ];

  readonly exposureSummaryCards = computed(() => {
    const rows = this.filteredExposure();
    if (!rows.length) return [];
    const totalAllocated = rows.reduce((sum, row) => sum + row.totalAllocated, 0);
    const outstanding = rows.reduce((sum, row) => sum + row.outstandingPrincipal, 0);
    const repaid = rows.reduce((sum, row) => sum + row.repaidPrincipal, 0);
    return [
      { key: 'allocated', label: this.t('payments.portfolio.totalAllocated'), value: totalAllocated },
      { key: 'outstanding', label: this.t('payments.portfolio.outstandingPrincipal'), value: outstanding },
      { key: 'repaid', label: this.t('payments.portfolio.repaidPrincipal'), value: repaid }
    ];
  });

  ngOnInit(): void {
    this.financeApi.listBooks().subscribe({
      next: books => {
        this.bookOptions.set(books.map(book => ({ label: `${book.code} — ${book.name}`, value: book.id })));
        this.postingBookId = books[0]?.id ?? '';
        if (this.postingBookId) this.onBookChanged();
      }
    });
    this.reload();
  }

  onTabChanged(tab: string | number | undefined): void {
    if (typeof tab === 'string' && tab.length > 0) {
      this.activeTab.set(tab);
    }
  }

  reload(): void {
    this.loading.set(true);
    this.error.set(null);
    this.actionError.set(null);

    let pending = 5;
    const done = (): void => {
      pending -= 1;
      if (pending <= 0) this.loading.set(false);
    };

    this.api.listFunderProfiles().subscribe({
      next: rows => { this.funders.set(rows); done(); },
      error: () => { this.error.set(this.t('payments.portfolio.errorLoad')); this.loading.set(false); }
    });
    this.api.listFundingPools(this.filterFunderId ?? undefined).subscribe({
      next: rows => { this.pools.set(rows); done(); },
      error: () => { this.error.set(this.t('payments.portfolio.errorLoad')); this.loading.set(false); }
    });
    this.loadAllocations(done);
    this.api.getFunderExposure(this.filterFunderId ?? undefined, this.filterCurrency || undefined).subscribe({
      next: rows => { this.exposure.set(rows); done(); },
      error: () => { this.error.set(this.t('payments.portfolio.errorLoad')); this.loading.set(false); }
    });
    this.loadDistributions(done);
    this.api.listLoanContracts().subscribe({
      next: rows => this.loanContracts.set(rows),
      error: () => { /* optional for allocate form */ }
    });
  }

  funderFilterOptions = computed(() =>
    this.funders().map(row => ({ label: `${row.funderCode} — ${row.displayName}`, value: row.id }))
  );

  funderSelectOptions = computed(() =>
    this.funders().filter(row => row.status === 'Active').map(row => ({ label: `${row.funderCode} — ${row.displayName}`, value: row.id }))
  );

  poolSelectOptions = computed(() =>
    this.pools().filter(row => row.status === 'Active').map(row => ({ label: `${row.poolCode} (${row.currencyCode})`, value: row.id }))
  );

  loanSelectOptions = computed(() =>
    this.loanContracts()
      .filter(row => row.status === 'Active')
      .map(row => ({
        label: `${this.formatAmount(row.principalAmount)} ${row.currencyCode} — ${this.statusLabel(row.status)}`,
        value: row.id
      }))
  );

  filteredFunders(): FunderProfileRow[] {
    return this.funders().filter(row => !this.filterStatus || row.status === this.filterStatus);
  }

  filteredPools(): FundingPoolRow[] {
    return this.pools().filter(row => {
      if (this.filterFunderId && row.funderId !== this.filterFunderId) return false;
      if (this.filterCurrency && row.currencyCode !== this.filterCurrency) return false;
      if (this.filterStatus && row.status !== this.filterStatus) return false;
      return true;
    });
  }

  filteredAllocations(): PortfolioAllocationRow[] {
    return this.allocations().filter(row => {
      if (this.filterFunderId && row.funderId !== this.filterFunderId) return false;
      if (this.filterCurrency && row.currencyCode !== this.filterCurrency) return false;
      if (this.filterStatus && row.status !== this.filterStatus) return false;
      return true;
    });
  }

  filteredExposure(): FunderExposureSummaryRow[] {
    return this.exposure().filter(row => {
      if (this.filterFunderId && row.funderId !== this.filterFunderId) return false;
      if (this.filterCurrency && row.currencyCode !== this.filterCurrency) return false;
      return true;
    });
  }

  filteredDistributions(): PortfolioRepaymentDistributionRow[] {
    return this.distributions().filter(row => {
      if (this.filterFunderId && row.funderId !== this.filterFunderId) return false;
      if (this.filterCurrency && row.currencyCode !== this.filterCurrency) return false;
      return true;
    });
  }

  funderCode(funderId: string): string {
    return this.funders().find(row => row.id === funderId)?.funderCode ?? funderId;
  }

  poolCode(poolId: string): string {
    return this.pools().find(row => row.id === poolId)?.poolCode ?? poolId;
  }

  openCreateFunderDialog(): void {
    this.funderModel = { funderCode: '', displayName: '', complianceReference: '' };
    this.actionError.set(null);
    this.createFunderDialogVisible = true;
  }

  openCreatePoolDialog(): void {
    this.poolModel = { funderId: this.filterFunderId ?? '', poolCode: '', currencyCode: this.filterCurrency || 'IRR', committedCapital: 10000 };
    this.actionError.set(null);
    this.createPoolDialogVisible = true;
  }

  openAllocateDialog(): void {
    this.allocateModel = {
      fundingPoolId: this.poolSelectOptions()[0]?.value ?? '',
      loanContractId: '',
      customerAccountId: '',
      principalAllocated: 0,
      allocationPercent: 100
    };
    this.postingCurrencyCode = this.filterCurrency || 'IRR';
    this.actionError.set(null);
    this.allocateDialogVisible = true;
  }

  onBookChanged(): void {
    if (!this.postingBookId) return;
    this.financeApi.listFiscalPeriods(this.postingBookId).subscribe({
      next: periods => {
        this.periodOptions.set(periods.map(period => ({
          label: `${period.fiscalYear}/${period.periodNumber} (${period.status})`,
          value: period.id
        })));
        this.postingPeriodId = periods.find(item => item.status.toLowerCase() === 'open')?.id ?? periods[0]?.id ?? '';
      }
    });
  }

  openWaterfallDialog(): void {
    const policy = this.selectedWaterfall();
    this.waterfallModel = policy
      ? { policyType: policy.policyType, policyJson: policy.policyJson }
      : { policyType: 'ProRata', policyJson: '{"strategy":"pro-rata"}' };
    this.actionError.set(null);
    this.waterfallDialogVisible = true;
  }

  createFunder(): void {
    if (!this.funderModel.funderCode.trim() || !this.funderModel.displayName.trim()) return;
    const payload: CreateFunderProfilePayload = {
      funderCode: this.funderModel.funderCode.trim(),
      displayName: this.funderModel.displayName.trim(),
      complianceReference: this.funderModel.complianceReference.trim() || 'ADMIN',
      riskAppetiteJson: '{}'
    };
    this.creatingFunder.set(true);
    this.api.createFunderProfile(payload).subscribe({
      next: created => {
        this.funders.update(rows => [created, ...rows]);
        this.funderModel = { funderCode: '', displayName: '', complianceReference: '' };
        this.createFunderDialogVisible = false;
        this.creatingFunder.set(false);
      },
      error: err => {
        this.actionError.set(this.parseApiError(err) ?? this.t('payments.portfolio.errorCreateFunder'));
        this.creatingFunder.set(false);
      }
    });
  }

  activateFunder(row: FunderProfileRow): void {
    this.api.activateFunderProfile(row.id).subscribe({
      next: updated => this.funders.update(rows => rows.map(item => item.id === row.id ? updated : item)),
      error: err => this.actionError.set(this.parseApiError(err) ?? this.t('payments.portfolio.errorActivate'))
    });
  }

  suspendFunder(row: FunderProfileRow): void {
    this.api.suspendFunderProfile(row.id).subscribe({
      next: updated => this.funders.update(rows => rows.map(item => item.id === row.id ? updated : item)),
      error: err => this.actionError.set(this.parseApiError(err) ?? this.t('payments.portfolio.errorSuspend'))
    });
  }

  createPool(): void {
    if (!this.poolModel.funderId || !this.poolModel.poolCode.trim()) return;
    const payload: CreateFundingPoolPayload = {
      funderId: this.poolModel.funderId,
      poolCode: this.poolModel.poolCode.trim(),
      currencyCode: this.poolModel.currencyCode,
      committedCapital: Number(this.poolModel.committedCapital),
      allocationPolicyJson: '{"strategy":"pro-rata"}',
      validFromUtc: new Date().toISOString(),
      idempotencyKey: this.newIdempotencyKey('admin-pool'),
      correlationId: 'admin-portfolio-ui'
    };
    this.creatingPool.set(true);
    this.api.createFundingPool(payload).subscribe({
      next: created => {
        this.pools.update(rows => [created, ...rows]);
        this.poolModel.poolCode = '';
        this.createPoolDialogVisible = false;
        this.creatingPool.set(false);
      },
      error: err => {
        this.actionError.set(this.parseApiError(err) ?? this.t('payments.portfolio.errorCreatePool'));
        this.creatingPool.set(false);
      }
    });
  }

  activatePool(row: FundingPoolRow): void {
    this.api.activateFundingPool(row.id).subscribe({
      next: updated => this.pools.update(rows => rows.map(item => item.id === row.id ? updated : item)),
      error: err => this.actionError.set(this.parseApiError(err) ?? this.t('payments.portfolio.errorActivate'))
    });
  }

  freezePool(row: FundingPoolRow): void {
    this.api.freezeFundingPool(row.id).subscribe({
      next: updated => this.pools.update(rows => rows.map(item => item.id === row.id ? updated : item)),
      error: err => this.actionError.set(this.parseApiError(err) ?? this.t('payments.portfolio.errorFreeze'))
    });
  }

  closePool(row: FundingPoolRow): void {
    this.api.closeFundingPool(row.id).subscribe({
      next: updated => this.pools.update(rows => rows.map(item => item.id === row.id ? updated : item)),
      error: err => this.actionError.set(this.parseApiError(err) ?? this.t('payments.portfolio.errorClose'))
    });
  }

  onPoolSelected(pool: FundingPoolRow | null): void {
    this.selectedPool.set(pool);
    this.selectedWaterfall.set(null);
    if (!pool) return;
    this.api.getPoolWaterfallPolicy(pool.id).subscribe({
      next: policy => this.selectedWaterfall.set(policy),
      error: () => this.selectedWaterfall.set(null)
    });
  }

  saveWaterfall(): void {
    const pool = this.selectedPool();
    if (!pool) return;
    const payload: UpsertFundingPoolWaterfallPolicyPayload = {
      policyType: this.waterfallModel.policyType,
      policyJson: this.waterfallModel.policyJson,
      effectiveFromUtc: new Date().toISOString()
    };
    this.savingWaterfall.set(true);
    this.api.upsertPoolWaterfallPolicy(pool.id, payload).subscribe({
      next: policy => {
        this.selectedWaterfall.set(policy);
        this.waterfallDialogVisible = false;
        this.savingWaterfall.set(false);
      },
      error: err => {
        this.actionError.set(this.parseApiError(err) ?? this.t('payments.portfolio.errorWaterfall'));
        this.savingWaterfall.set(false);
      }
    });
  }

  onLoanSelected(loanContractId: string): void {
    const loan = this.loanContracts().find(row => row.id === loanContractId);
    if (!loan) return;
    this.allocateModel.customerAccountId = loan.customerAccountId;
    this.allocateModel.principalAllocated = loan.principalAmount;
    this.postingCurrencyCode = loan.currencyCode;
    const pool = this.pools().find(row => row.id === this.allocateModel.fundingPoolId);
    if (pool && pool.currencyCode !== loan.currencyCode) {
      this.actionError.set(this.t('payments.portfolio.errorCurrencyMismatch'));
    }
  }

  allocateLoan(): void {
    const loan = this.loanContracts().find(row => row.id === this.allocateModel.loanContractId);
    const pool = this.pools().find(row => row.id === this.allocateModel.fundingPoolId);
    if (!loan || !pool) return;
    if (pool.currencyCode !== loan.currencyCode) {
      this.actionError.set(this.t('payments.portfolio.errorCurrencyMismatch'));
      return;
    }
    if (!this.postingBookId || !this.postingPeriodId || !this.postingDebitAccountId || !this.postingCreditAccountId) {
      this.actionError.set(this.t('payments.financialCompleteness.errorPostingProfile'));
      return;
    }

    const postingProfile = {
      bookId: this.postingBookId,
      periodId: this.postingPeriodId,
      debitAccountId: this.postingDebitAccountId,
      creditAccountId: this.postingCreditAccountId,
      currencyCode: this.postingCurrencyCode
    };
    const body = {
      fundingPoolId: pool.id,
      loanContractId: loan.id,
      creditApplicationId: loan.creditApplicationId,
      customerAccountId: loan.customerAccountId,
      principalAllocated: Number(this.allocateModel.principalAllocated),
      currencyCode: loan.currencyCode,
      allocationPercent: Number(this.allocateModel.allocationPercent),
      correlationId: 'admin-portfolio-ui',
      postFinancialEntry: true,
      postingProfile
    };
    const idempotencyKey = this.newIdempotencyKey('admin-alloc');
    const idempotencyPayloadHash = this.buildPayloadHash({ ...body, idempotencyKey });

    this.creatingAllocation.set(true);
    this.api.allocateLoanToPool({ ...body, idempotencyKey, idempotencyPayloadHash }).subscribe({
      next: result => {
        this.allocations.update(rows => [result.allocation, ...rows.filter(item => item.id !== result.allocation.id)]);
        this.allocateDialogVisible = false;
        this.creatingAllocation.set(false);
        this.reload();
      },
      error: err => {
        this.actionError.set(this.parseApiError(err) ?? this.t('payments.portfolio.errorAllocate'));
        this.creatingAllocation.set(false);
      }
    });
  }

  releaseAllocation(row: PortfolioAllocationRow): void {
    this.api.releasePortfolioAllocation(row.id).subscribe({
      next: updated => this.allocations.update(rows => rows.map(item => item.id === row.id ? updated : item)),
      error: err => this.actionError.set(this.parseApiError(err) ?? this.t('payments.portfolio.errorRelease'))
    });
  }

  onAllocationPage(event: TableLazyLoadEvent): void {
    const rows = event.rows ?? this.allocationPageSize;
    const page = Math.floor((event.first ?? 0) / rows) + 1;
    if (page === this.allocationPage && rows === this.allocationPageSize) {
      return;
    }

    this.allocationPage = page;
    this.allocationPageSize = rows;
    this.loadAllocations();
  }

  onDistributionPage(event: TableLazyLoadEvent): void {
    const rows = event.rows ?? this.distributionPageSize;
    const page = Math.floor((event.first ?? 0) / rows) + 1;
    if (page === this.distributionPage && rows === this.distributionPageSize) {
      return;
    }

    this.distributionPage = page;
    this.distributionPageSize = rows;
    this.loadDistributions();
  }

  private loadAllocations(onComplete?: () => void): void {
    this.api.listPortfolioAllocations({
      funderId: this.filterFunderId ?? undefined,
      status: this.filterStatus || undefined,
      page: this.allocationPage,
      pageSize: this.allocationPageSize
    }).subscribe({
      next: response => {
        this.allocations.set(response.items);
        this.allocationTotalRecords = response.totalCount;
        onComplete?.();
      },
      error: () => {
        if (onComplete) {
          this.error.set(this.t('payments.portfolio.errorLoad'));
          this.loading.set(false);
          return;
        }

        this.actionError.set(this.t('payments.portfolio.errorLoad'));
      }
    });
  }

  private loadDistributions(onComplete?: () => void): void {
    this.api.listRepaymentDistributions({
      funderId: this.filterFunderId ?? undefined,
      page: this.distributionPage,
      pageSize: this.distributionPageSize
    }).subscribe({
      next: response => {
        this.distributions.set(response.items);
        this.distributionTotalRecords = response.totalCount;
        onComplete?.();
      },
      error: () => {
        if (onComplete) {
          this.error.set(this.t('payments.portfolio.errorLoad'));
          this.loading.set(false);
          return;
        }

        this.actionError.set(this.t('payments.portfolio.errorLoad'));
      }
    });
  }

  statusLabel(status: string): string {
    const key = paymentStatusLabelKey(status);
    const translated = this.t(key);
    return translated === key ? status : translated;
  }

  statusSeverity(status: string): 'success' | 'warn' | 'danger' | 'info' {
    if (status === 'Active' || status === 'Settled') return 'success';
    if (status === 'Suspended' || status === 'Frozen' || status === 'Draft') return 'warn';
    if (status === 'Closed' || status === 'Released' || status === 'Defaulted') return 'danger';
    return 'info';
  }

  t(key: string): string {
    return this.localizationService.translate(key);
  }

  formatAmount(value: number): string {
    return new Intl.NumberFormat(this.localizationService.activeCulture(), {
      maximumFractionDigits: 0
    }).format(value);
  }

  private parseApiError(err: unknown): string | null {
    const body = (err as { error?: { error?: string; Error?: string } })?.error;
    return body?.error ?? body?.Error ?? null;
  }

  private newIdempotencyKey(prefix: string): string {
    return `${prefix}-${crypto.randomUUID()}`;
  }

  private buildPayloadHash(payload: unknown): string {
    return btoa(JSON.stringify(payload)).slice(0, 128);
  }
}
